import { getExpectedAssists, getExpectedGoals } from "./eventMetrics";
import type { GPEventResponse } from "./types";

const SHOT_EVENT_TYPE = "shot";
const GOAL_EVENT_TYPE = "goal";
const PASS_EVENT_TYPE = "pass";
const RESTART_DETAIL_KEY = "restart";
const PENALTY_DETAIL_KEY = "penalty";
const GOAL_TYPE_DETAIL_KEY = "goal_type";
const PERIOD_KEY_WITHOUT_HALF = "--";
const RELATED_EVENT_TIME_TOLERANCE_MS = 1000;

const PENALTY_RESTART_VALUES = new Set<string>(["penalty", "shootout"]);
const PENALTY_GOAL_TYPES = new Set<string>(["penalty", "shootout"]);

/** Values the taggers can attach to `restart`; anything else means we cannot tell penalties apart. */
const KNOWN_RESTART_VALUES = new Set<string>([
	"no",
	"corner",
	"corner-kick",
	"free-kick",
	"direct-free-kick",
	"indirect-free-kick",
	"goal-kick",
	"throw-in",
	"penalty",
	"shootout",
]);

export interface ExpectedGoalsAggregate {
	expectedGoals: number;
	expectedAssists: number;
	nonPenaltyExpectedGoals: number;
	nonPenaltyShotsWithExpectedGoals: number;
}

export function hasExpectedGoalsEventData(events: GPEventResponse[]): boolean {
	return events.some(
		(event) => getExpectedGoals(event.details) !== null || getExpectedAssists(event.details) !== null,
	);
}

function getDetailString(event: GPEventResponse, key: string): string | null {
	const details = event.details;

	if (details === null || typeof details !== "object") {
		return null;
	}

	const detailValue = (details as Record<string, unknown>)[key];

	if (typeof detailValue !== "string" || detailValue === "") {
		return null;
	}

	return detailValue;
}

function getDetailBoolean(event: GPEventResponse, key: string): boolean | null {
	const details = event.details;

	if (details === null || typeof details !== "object") {
		return null;
	}

	const detailValue = (details as Record<string, unknown>)[key];

	return typeof detailValue === "boolean" ? detailValue : null;
}

export function isEventInSelectedPeriods(event: GPEventResponse, periods: string[]): boolean {
	const eventPeriod = event.half == null ? PERIOD_KEY_WITHOUT_HALF : event.half.toString();

	return periods.includes(eventPeriod);
}

function isPenaltyShot(event: GPEventResponse): boolean {
	if (event.type !== SHOT_EVENT_TYPE) {
		return false;
	}

	if (getDetailBoolean(event, PENALTY_DETAIL_KEY) === true) {
		return true;
	}

	const restart = getDetailString(event, RESTART_DETAIL_KEY);

	return restart !== null && PENALTY_RESTART_VALUES.has(restart);
}

function hasUnrecognizedRestart(event: GPEventResponse): boolean {
	const restart = getDetailString(event, RESTART_DETAIL_KEY);

	return restart !== null && KNOWN_RESTART_VALUES.has(restart) === false;
}

function isPenaltyGoal(event: GPEventResponse): boolean {
	if (event.type !== GOAL_EVENT_TYPE) {
		return false;
	}

	const goalType = getDetailString(event, GOAL_TYPE_DETAIL_KEY);

	return goalType !== null && PENALTY_GOAL_TYPES.has(goalType);
}

export function addExpectedGoalsToGoalEvents(
	goals: GPEventResponse[],
	shots: GPEventResponse[],
): GPEventResponse[] {
	return goals.map((goal) => {
		if (getExpectedGoals(goal.details) !== null || goal.videoTime === null) {
			return goal;
		}

		const relatedShot = shots.find((shot) => {
			if (
				shot.gamePlayerId !== goal.gamePlayerId
				|| shot.videoTime === null
				|| getDetailBoolean(shot, "goal") !== true
			) {
				return false;
			}

			return (
				shot.videoTime > goal.videoTime - RELATED_EVENT_TIME_TOLERANCE_MS
				&& shot.videoTime < goal.videoTime + RELATED_EVENT_TIME_TOLERANCE_MS
			);
		});
		const expectedGoals = getExpectedGoals(relatedShot?.details);

		if (expectedGoals === null) {
			return goal;
		}

		return {
			...goal,
			details: {
				...(goal.details ?? {}),
				xg: expectedGoals,
			},
		};
	});
}

/**
 * Non-penalty metrics are only trustworthy when every shot carries a restart value we understand and
 * penalty goals are backed by a penalty-tagged shot. Otherwise penalties cannot be excluded reliably.
 */
export function isPenaltyDetectionReliable(events: GPEventResponse[], periods: string[]): boolean {
	let penaltyShotCount = 0;
	let penaltyGoalCount = 0;

	for (const event of events) {
		if (isEventInSelectedPeriods(event, periods) === false) {
			continue;
		}

		if (event.type === SHOT_EVENT_TYPE && hasUnrecognizedRestart(event)) {
			return false;
		}

		if (isPenaltyShot(event)) {
			penaltyShotCount += 1;
		}

		if (isPenaltyGoal(event)) {
			penaltyGoalCount += 1;
		}
	}

	return penaltyGoalCount === 0 || penaltyShotCount > 0;
}

/**
 * Expected goals are read from shot events only and expected assists from pass events only, so goals
 * (which are tagged as separate events on top of the shot) are never counted twice.
 */
export function aggregateExpectedGoals(
	events: GPEventResponse[],
	periods: string[],
	isOwnEvent: (event: GPEventResponse) => boolean,
): ExpectedGoalsAggregate {
	const aggregate: ExpectedGoalsAggregate = {
		expectedGoals: 0,
		expectedAssists: 0,
		nonPenaltyExpectedGoals: 0,
		nonPenaltyShotsWithExpectedGoals: 0,
	};

	events.forEach((event) => {
		if (isEventInSelectedPeriods(event, periods) === false || isOwnEvent(event) === false) {
			return;
		}

		if (event.type === PASS_EVENT_TYPE) {
			const expectedAssists = getExpectedAssists(event.details);

			if (expectedAssists !== null) {
				aggregate.expectedAssists += expectedAssists;
			}
		}

		if (event.type !== SHOT_EVENT_TYPE) {
			return;
		}

		const expectedGoals = getExpectedGoals(event.details);

		if (expectedGoals === null) {
			return;
		}

		aggregate.expectedGoals += expectedGoals;

		if (isPenaltyShot(event) === false) {
			aggregate.nonPenaltyExpectedGoals += expectedGoals;
			aggregate.nonPenaltyShotsWithExpectedGoals += 1;
		}
	});

	return aggregate;
}
